import { useMemo } from 'react';
import { useLocation, useMatch, matchPath } from 'react-router-dom';
import { z } from 'zod';
import { ChatKey } from './util/getChatKey';
import { decodeSearchParams, encodeSearchParams } from '@pdcplan/shared/url';
import { intStringCoerced } from '@pdcplan/shared/schema';
/*
When siteRoutes are defined as a function it means they contain one or more id's that needs to
be given as a parameter for the path to match.
Use the function with empty parameters to get the match string needed for the router.
(Remember therefore to add default values to all id's that needs to be matched as ":id");
*/

/**
 * Values are used in audit logging, so they should not be changed.
 */
export enum RouteEnum {
  Team = 'team',
  Teams = 'teams',
  Login = 'login',
  Chat = 'chat',
}

export type RouteState =
  | {
      route: RouteEnum.Team;
      teamId: string;
      params: TeamViewQueries;
    }
  | { route: RouteEnum.Teams }
  | { route: RouteEnum.Login }
  | {
      route: RouteEnum.Chat;
      teamId: string;
      params: MessageViewQueries;
    };

export function getRouteState(location: {
  pathname: string;
  search: string;
}): RouteState | null {
  if (location.pathname.startsWith('/teams')) {
    return { route: RouteEnum.Teams };
  }
  if (location.pathname.startsWith('/team')) {
    const params = getTeamRouteParams(location);
    const teamId = matchPath(
      getSiteRoutes().team(),
      location.pathname
    )?.params.teamId?.replace(':teamId', '');
    if (!teamId) {
      return null;
    }
    return { route: RouteEnum.Team, teamId, params };
  }
  if (location.pathname.startsWith('/login')) {
    return { route: RouteEnum.Login };
  }
  if (location.pathname.startsWith('/chat')) {
    const params = getMessageRouteParams(location);
    const teamId = matchPath(
      getSiteRoutes().chat(),
      location.pathname
    )?.params.teamId?.replace(':teamId', '');
    if (!teamId) {
      return null;
    }
    return { route: RouteEnum.Chat, teamId, params };
  }
  return null;
}

// These enums have to be string enums, as they will be part of the route urls
export enum ListViewTab {
  Planned = 'planned',
  Pending = 'pending',
  Changes = 'changes',
}

export enum MessageViewTab {
  Read = 'read',
  Unread = 'unread',
}

export enum TeamViewMode {
  FindSubstitute = 'findsubstitute',
  CreateOrEdit = 'edit',
}

export enum ChatType {
  Employee = 'employee',
  Private = 'private',
}

const teamViewQueriesSchema = z.object({
  personId: intStringCoerced.optional().catch(undefined),
  shiftId: z.string().optional(),
  tab: z.enum(ListViewTab).optional().catch(undefined),
  mode: z.enum(TeamViewMode).optional().catch(undefined),
  search: z.string().optional(),
  chat: z.enum(ChatType).optional().catch(undefined),
});

export type TeamViewQueries = z.infer<typeof teamViewQueriesSchema>;

const messageViewQueriesSchema = z.object({
  thread: (z.string() as unknown as z.ZodType<ChatKey>).optional(),
  tab: z.enum(MessageViewTab).optional().catch(undefined),
});

export type MessageViewQueries = z.infer<typeof messageViewQueriesSchema>;

/**
 *
 */
export function getTeamRouteParams(location: {
  pathname: string;
  search: string;
}) {
  return decodeSearchParams(teamViewQueriesSchema, location);
}

export function useTeamRouteParams() {
  const location = useLocation();
  return useMemo(() => getTeamRouteParams(location), [location]);
}

/**
 * Should only return an empty string if the current page is not the /team or /chat page
 * @returns The teamId from the url
 */
export function useTeamId() {
  const teamMatch = useMatch(getSiteRoutes().team());
  const chatMatch = useMatch(getSiteRoutes().chat());
  return (
    (teamMatch?.params.teamId ?? chatMatch?.params.teamId)?.replace(
      ':teamId',
      ''
    ) ?? ''
  );
}

export function useRouteState() {
  const location = useLocation();
  return useMemo(() => getRouteState(location), [location]);
}

/**
 *
 */
export function getMessageRouteParams(location: {
  pathname: string;
  search: string;
}) {
  return decodeSearchParams(messageViewQueriesSchema, location);
}

export function useMessageRouteParams() {
  const location = useLocation();
  return useMemo(() => getMessageRouteParams(location), [location]);
}

const siteRoutes = {
  login: '/login',
  teams: '/teams',
  chat: (teamId = ':teamId', queries?: MessageViewQueries) => {
    const params = encodeSearchParams(queries);
    return `/chat/${teamId}${params ? '?' : ''}${params}` as const;
  },
  team: (teamId = ':teamId', queries?: TeamViewQueries) => {
    const params = encodeSearchParams(queries);
    return `/team/${teamId}${params ? '?' : ''}${params}` as const;
  },
} as const;

export function getSiteRoutes() {
  return siteRoutes;
}

